import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  INSURANCE_PROVIDER_PLACEHOLDER,
  MANUAL_RELATIONSHIP_OPTIONS,
  buildRecheckInsuranceEstimatePayload,
  buildInsuranceManualFormFromRegistrationData,
  fetchRegistrationInsuranceProviders,
  findPayerIdByProviderName,
  formatDobForDateInput,
  getInsuranceOptionLabel,
  getInsuranceProviderLabel,
  mapManualRelationshipToCode,
  resolveManualProviderInsuranceId,
  resolveProviderOptionValue,
} from "../insuranceProviders";
import {
  hasRequiredInsuranceCardImages,
  setInsuranceCardImages,
  getInsuranceCardImages,
} from "../insuranceCardImagesStore";
import RegistrationInsuranceCardUpload from "../RegistrationInsuranceCardUpload";
import { getRegistrationData, updateRegistrationData } from "../../registrationSetupStorage";
import RegistrationFormSelect from "../../../RegistrationLayout/RegistrationFormSelect";
import RegistrationDobField from "../../../RegistrationLayout/RegistrationDobField";
import {
  clearInsuranceDetailsReturnToCoverage,
  registrationStepBack,
} from "../../registrationNavigation";
import {
  getInsuranceInformationValidationError,
  getInsuranceHolderDobValidationError,
  getInsuranceMemberDobValidationError,
  isInsuranceInformationFormComplete,
} from "./RegistrationInsuranceInformation.validator";
import { resolveRegisterValidationMessage } from "../../../Register/Register.validator";

const INSURANCE_INFO_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_INFORMATION;

const RegistrationInsuranceInformation = ({ history }) => {
  const { t, i18n } = useTranslation();
  const [insuranceProviders, setInsuranceProviders] = useState([
    INSURANCE_PROVIDER_PLACEHOLDER,
  ]);
  const [insuranceProvidersLoading, setInsuranceProvidersLoading] =
    useState(true);
  const [cardImagesSubmitAttempted, setCardImagesSubmitAttempted] =
    useState(false);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const [hasCardImages, setHasCardImages] = useState(() =>
    hasRequiredInsuranceCardImages()
  );
  const [form, setForm] = useState({
    provider: "",
    firstName: "",
    lastName: "",
    dob: "",
    memberId: "",
    groupNumber: "",
    relationship: "",
    holderFirstName: "",
    holderLastName: "",
    holderDob: "",
  });

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const saved = localStorage.getItem("data");
    if (!saved) {
      history.replace("/register/account/payment");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    try {
      const parsed = JSON.parse(saved);
      if (!parsed.firstName || !parsed.email) {
        history.replace("/register/account");
      } else {
        const manualForm = buildInsuranceManualFormFromRegistrationData(parsed);
        setForm((prev) => ({
          ...prev,
          firstName: String(parsed.firstName || "").trim(),
          lastName: String(parsed.lastName || "").trim(),
          dob: formatDobForDateInput(parsed.dob),
          ...(manualForm || {}),
        }));
      }
    } catch {
      history.replace("/register/account");
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  useEffect(() => {
    const failureMessage =
      getRegistrationData().insuranceVerificationFailureMessage;
    if (!failureMessage) {
      return;
    }
    message.error(failureMessage);
    updateRegistrationData({ insuranceVerificationFailureMessage: null });
  }, []);

  useEffect(() => {
    const parsed = getRegistrationData();
    const registrationCode = parsed.registrationCode?.trim();

    if (!registrationCode) {
      message.error(t("NUTRITION_INSMANUAL_ERR_REG_CODE_REQUIRED"));
      history.replace("/register/account");
      setInsuranceProvidersLoading(false);
      return undefined;
    }

    if (
      Array.isArray(parsed.insuranceProviderOptions) &&
      parsed.insuranceProviderOptions.length > 1
    ) {
      setInsuranceProviders(parsed.insuranceProviderOptions);
    }

    let cancelled = false;
    const controller = new AbortController();
    setInsuranceProvidersLoading(true);

    fetchRegistrationInsuranceProviders({
      registrationCode,
      language: i18n.language,
      apiService: AuthenticationApiSerivce,
      signal: controller.signal,
    })
      .then(({ list, options }) => {
        if (cancelled) {
          return;
        }

        setInsuranceProviders(options);
        updateRegistrationData({
          insuranceProviderOptions: options,
          insuranceListItems: list,
        });

        setForm((prev) => {
          const hydrated = buildInsuranceManualFormFromRegistrationData(
            parsed,
            list
          );
          const base = hydrated
            ? { ...prev, ...hydrated }
            : prev;
          const provider = resolveProviderOptionValue(base.provider, list);
          return provider === base.provider ? base : { ...base, provider };
        });
      })
      .catch((err) => {
        if (
          cancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
        message.error(
          err?.message?.startsWith?.("NUTRITION_")
            ? t(err.message)
            : err?.message || t("NUTRITION_INSMANUAL_ERR_LOAD_PROVIDERS")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setInsuranceProvidersLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language, t]);

  const showHolderFields =
    form.relationship !== "" && form.relationship !== "self";

  const memberDobValue = String(form.dob || "").trim();
  const isTypingPartialMemberDob =
    memberDobValue.length > 0 &&
    memberDobValue.length < 10 &&
    /^[\d/]+$/.test(memberDobValue);
  const memberDobErrorKey = getInsuranceMemberDobValidationError(
    form.dob,
    formSubmitAttempted ||
      (Boolean(memberDobValue) && !isTypingPartialMemberDob)
  );

  const holderDobValue = String(form.holderDob || "").trim();
  const isTypingPartialHolderDob =
    holderDobValue.length > 0 &&
    holderDobValue.length < 10 &&
    /^[\d/]+$/.test(holderDobValue);
  const holderDobErrorKey = showHolderFields
    ? getInsuranceHolderDobValidationError(
        form.holderDob,
        formSubmitAttempted ||
          (Boolean(holderDobValue) && !isTypingPartialHolderDob)
      )
    : null;

  const accountData = getRegistrationData();
  const canRecheck =
    isInsuranceInformationFormComplete(form, showHolderFields) &&
    hasCardImages &&
    !insuranceProvidersLoading;

  const handleRecheck = () => {
    setFormSubmitAttempted(true);
    const validationMessage = getInsuranceInformationValidationError(
      form,
      showHolderFields
    );
    if (validationMessage) {
      message.warning(resolveRegisterValidationMessage(validationMessage, t));
      return;
    }

    if (!hasRequiredInsuranceCardImages()) {
      setCardImagesSubmitAttempted(true);
      return;
    }

    const {
      provider,
      firstName,
      lastName,
      dob,
      memberId,
      groupNumber,
      relationship,
      holderFirstName,
      holderLastName,
      holderDob,
    } = form;

    const insuranceManual = {
      provider,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob,
      memberId: memberId.trim(),
      groupNumber: groupNumber.trim(),
      relationship,
      holderFirstName: holderFirstName.trim(),
      holderLastName: holderLastName.trim(),
      holderDob,
    };

    const recheckPayload = buildRecheckInsuranceEstimatePayload(
      insuranceManual,
      accountData
    );
    if (!recheckPayload) {
      message.warning(t("NUTRITION_INSMANUAL_ERR_INVALID_PROVIDER"));
      return;
    }

    const insuranceId = resolveManualProviderInsuranceId(provider, accountData);
    const providerName =
      getInsuranceProviderLabel(String(insuranceId), insuranceProviders) ||
      provider;
    const isSelf = relationship === "self";

    setInsuranceCardImages(getInsuranceCardImages());
    clearInsuranceDetailsReturnToCoverage();
    updateRegistrationData({
      insuranceManual,
      insuranceVerification: {
        payer: insuranceId
          ? String(insuranceId)
          : findPayerIdByProviderName(provider, accountData),
        providerName,
        memberId: memberId.trim(),
        groupNumber: groupNumber.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        relationship: mapManualRelationshipToCode(relationship),
        dob,
        policyHolderFirstName: isSelf ? "" : holderFirstName.trim(),
        policyHolderLastName: isSelf ? "" : holderLastName.trim(),
        policyHolderDob: isSelf ? "" : holderDob,
      },
      paymentType: "insurance",
      insuranceRecheck: true,
      insuranceEligibilityResult: undefined,
      coverageResult: undefined,
      insuranceVerificationFailureMessage: null,
    });
    history.push("/register/account/insurance/checking");
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={INSURANCE_INFO_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <div className="content-card-scroll">
              <h2 className="section-title">
                {t("NUTRITION_INSMANUAL_LBL_TITLE")}
              </h2>
              <p className="section-sub">
                {t("NUTRITION_INSMANUAL_DESC_SUBTITLE")}
              </p>

              <div className="field-group-label">
                {t("NUTRITION_INSMANUAL_LBL_GROUP_PLAN")}
              </div>

              <div className="insurance-details-grid">
                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="ins-provider">
                    {t("NUTRITION_COMMON_LBL_INSURANCE_PROVIDER")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationFormSelect
                    id="ins-provider"
                    value={form.provider}
                    disabled={insuranceProvidersLoading}
                    onChange={(e) =>
                      setForm({ ...form, provider: e.target.value })
                    }
                  >
                    {insuranceProviders.map((opt) => (
                      <option key={opt.value || "empty"} value={opt.value}>
                        {getInsuranceOptionLabel(opt, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ins-first-name">
                    {t("NUTRITION_INSDISC_LBL_FIRST_NAME")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <input
                    id="ins-first-name"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_FIRST_NAME")}
                    value={form.firstName}
                    onChange={(e) =>
                      setForm({ ...form, firstName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ins-last-name">
                    {t("NUTRITION_INSDISC_LBL_LAST_NAME")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <input
                    id="ins-last-name"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_LAST_NAME")}
                    value={form.lastName}
                    onChange={(e) =>
                      setForm({ ...form, lastName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="ins-dob">
                    {t("DATE_OF_BIRTH")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationDobField
                    id="ins-dob"
                    value={form.dob}
                    onChange={(formatted) =>
                      setForm({ ...form, dob: formatted })
                    }
                  />
                  {memberDobErrorKey ? (
                    <div className="text-danger">
                      {resolveRegisterValidationMessage(memberDobErrorKey, t)}
                    </div>
                  ) : null}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ins-member-id">
                    {t("MEMBER_ID")} <span className="required-mark">*</span>
                  </label>
                  <input
                    id="ins-member-id"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_MEMBER_ID")}
                    value={form.memberId}
                    onChange={(e) =>
                      setForm({ ...form, memberId: e.target.value })
                    }
                  />
                  <div className="form-hint">
                    {t("NUTRITION_INSMANUAL_HINT_MEMBER_ID")}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ins-group">
                    {t("NUTRITION_COMMON_LBL_GROUP_NUMBER")}
                  </label>
                  <input
                    id="ins-group"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_GROUP_NUMBER")}
                    value={form.groupNumber}
                    onChange={(e) =>
                      setForm({ ...form, groupNumber: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="divider" />

              <div className="field-group-label">
                {t("NUTRITION_INSMANUAL_LBL_GROUP_HOLDER")}
              </div>

              <div className="insurance-details-grid">
                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="ins-relationship">
                    {t("NUTRITION_INSMANUAL_LBL_RELATIONSHIP")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationFormSelect
                    id="ins-relationship"
                    value={form.relationship}
                    onChange={(e) =>
                      setForm({ ...form, relationship: e.target.value })
                    }
                  >
                    {MANUAL_RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt.value || "empty"} value={opt.value}>
                        {getInsuranceOptionLabel(opt, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                </div>

                {showHolderFields && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="ins-holder-first">
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_FIRST_NAME")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <input
                        id="ins-holder-first"
                        type="text"
                        className="form-input"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_FIRST_NAME")}
                        value={form.holderFirstName}
                        onChange={(e) =>
                          setForm({ ...form, holderFirstName: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="ins-holder-last">
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_LAST_NAME")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <input
                        id="ins-holder-last"
                        type="text"
                        className="form-input"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_LAST_NAME")}
                        value={form.holderLastName}
                        onChange={(e) =>
                          setForm({ ...form, holderLastName: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group insurance-details-span-2">
                      <label className="form-label" htmlFor="ins-holder-dob">
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_DOB")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <RegistrationDobField
                        id="ins-holder-dob"
                        value={form.holderDob}
                        onChange={(formatted) =>
                          setForm({ ...form, holderDob: formatted })
                        }
                      />
                      {holderDobErrorKey ? (
                        <div className="text-danger">
                          {resolveRegisterValidationMessage(holderDobErrorKey, t)}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}

                <RegistrationInsuranceCardUpload
                  forceShowErrors={cardImagesSubmitAttempted}
                  idPrefix="manual-insurance-card"
                  onImagesChange={setHasCardImages}
                />
              </div>

              <div className="notice-box" style={{ marginTop: 8 }}>
                <div className="notice-icon">🔒</div>
                <div className="notice-text">
                  {t("NUTRITION_INSMANUAL_DESC_NOTICE")}
                </div>
              </div>

              <button
                type="button"
                className="btn-primary"
                onClick={handleRecheck}
                disabled={!canRecheck}
              >
                {t("NUTRITION_INSMANUAL_BTN_UPDATE")}
              </button>
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationInsuranceInformation);
